import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import 'cap-widget';
import { environment } from '../../../../environments/environment';
import type { CapWidgetElement, CapSolveEvent, CapErrorEvent } from './turnstile.types';

/**
 * Reusable CAPTCHA widget component backed by self-hosted Cap (proof-of-work).
 *
 * Replaces Cloudflare Turnstile. The `cap-widget` web component is bundled via
 * npm (served from our own origin, trusted by CSP 'strict-dynamic'); it points at
 * our Cap instance via `data-cap-api-endpoint = <capApiEndpoint>/<siteKey>/` and
 * emits a `solve` CustomEvent carrying the token. The public contract of this
 * component (selector, inputs, `tokenReceived`/`tokenExpired`/`errorOccurred`
 * outputs, `reset()`, and the 8 s fail-open fallback) is unchanged, so the
 * login/register dialogs consume it exactly as before.
 *
 * If no site key is configured (empty string in environment), the widget is not
 * rendered and `tokenReceived` emits an empty string immediately to allow form
 * submission without CAPTCHA (dev/local).
 *
 * @example
 * ```html
 * <app-turnstile-widget
 *   (tokenReceived)="onToken($event)"
 *   (tokenExpired)="onExpired()"
 * />
 * ```
 */
@Component({
  selector: 'app-turnstile-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #turnstileContainer class="turnstile-container"></div>`,
  styles: `
    .turnstile-container {
      display: flex;
      justify-content: center;
      min-height: 0;
    }
  `,
})
export class TurnstileWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly ngZone = inject(NgZone);

  /** Override the site key from environment (useful for testing). */
  readonly siteKey = input<string>(environment.turnstileSiteKey);

  /** Kept for API compatibility with the previous Turnstile widget; unused by Cap. */
  readonly action = input<string>('register');

  /** Emitted when the challenge is solved successfully. */
  readonly tokenReceived = output<string>();

  /** Emitted when a previously issued token expires / the widget resets. */
  readonly tokenExpired = output<void>();

  /** Emitted when the widget encounters an error. */
  readonly errorOccurred = output<string>();

  private readonly container = viewChild.required<ElementRef<HTMLElement>>('turnstileContainer');

  private widget: CapWidgetElement | null = null;
  private readonly enabled = !!environment.turnstileSiteKey;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private solved = false;
  private destroyed = false;

  /** Max time (ms) to wait for a solve before letting the form submit without CAPTCHA. */
  private static readonly FALLBACK_TIMEOUT_MS = 8_000;

  ngAfterViewInit(): void {
    if (!this.enabled) {
      // No site key → skip CAPTCHA (dev/local). Emit empty token so form enables.
      this.tokenReceived.emit('');
      return;
    }
    this.startFallbackTimer();
    this.renderWidget();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearFallbackTimer();
    this.removeWidget();
  }

  /** Reset the widget to allow a new challenge (e.g. after submission error). */
  reset(): void {
    if (!this.enabled) {
      this.tokenReceived.emit('');
      return;
    }
    this.solved = false;
    if (this.widget?.reset) {
      this.widget.reset();
    } else {
      // No reset() on the element → recreate it.
      this.removeWidget();
      this.renderWidget();
    }
  }

  private get endpoint(): string {
    const key = this.siteKey() || environment.turnstileSiteKey;
    return `${environment.capApiEndpoint}/${key}/`;
  }

  private renderWidget(): void {
    const key = this.siteKey() || environment.turnstileSiteKey;
    if (!key) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const el = document.createElement('cap-widget') as CapWidgetElement;
      el.setAttribute('data-cap-api-endpoint', this.endpoint);
      el.addEventListener('solve', (e: Event) => {
        this.solved = true;
        this.clearFallbackTimer();
        const token = (e as CapSolveEvent).detail?.token ?? '';
        this.ngZone.run(() => this.tokenReceived.emit(token));
      });
      el.addEventListener('error', (e: Event) => {
        const message = (e as CapErrorEvent).detail?.message ?? 'error';
        this.ngZone.run(() => this.errorOccurred.emit(message));
      });
      el.addEventListener('reset', () => {
        this.solved = false;
        this.ngZone.run(() => this.tokenExpired.emit());
      });
      this.widget = el;
      this.container().nativeElement.appendChild(el);
    });
  }

  private removeWidget(): void {
    if (this.widget) {
      this.widget.remove();
      this.widget = null;
    }
  }

  private startFallbackTimer(): void {
    this.fallbackTimer = setTimeout(() => {
      this.fallbackTimer = null;
      // Widget didn't solve in time (Cap unreachable / slow) — let the user through.
      if (!this.solved && !this.destroyed) {
        console.warn('Cap: fallback activated — allowing form submission without CAPTCHA');
        this.ngZone.run(() => this.tokenReceived.emit(''));
      }
    }, TurnstileWidgetComponent.FALLBACK_TIMEOUT_MS);
  }

  private clearFallbackTimer(): void {
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }
}
